import {
  Alert,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { MonitoringPanelDto } from '@app/shared';
import { FilterPanel } from '../../components/FilterPanel';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { api } from '../../lib/api';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';
import { AlertSettingsDialog } from './AlertSettingsDialog';

const FIVE_MINUTES = 5 * 60_000;
const todayIso = () => new Date().toISOString().slice(0, 10);
const DATE = { slotProps: { inputLabel: { shrink: true } } } as const;

function DayTable({
  title,
  rows,
}: {
  title: string;
  rows: MonitoringPanelDto['scheduledAutopay'];
}) {
  return (
    <Paper variant="outlined" component="section" aria-label={title} sx={{ flex: '1 1 320px' }}>
      <Typography variant="h3" component="h2" sx={{ px: 1.5, py: 1 }}>
        {title}
      </Typography>
      <Table size="small" aria-label={title}>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Expiry Date</TableCell>
            <TableCell align="right">Total Records</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3}>No Record Found</TableCell>
            </TableRow>
          ) : (
            rows.map((r, i) => (
              <TableRow key={r.expiryDate}>
                <TableCell>{i + 1}</TableCell>
                <TableCell>{r.expiryDate}</TableCell>
                <TableCell align="right">{r.total}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}

/**
 * "System Health Status" > "Monitoring Panel" (release switch `system-health`): Activity Success
 * Rate for the dates chosen, Scheduled Autopay and Scheduled Renewals; it reads itself again
 * every 5 minutes, as the reference says it does. For administrators.
 */
export function MonitoringPanelPage() {
  const { user, can } = useAuth();
  const featureOn = useFeatureReleased();
  const [alerts, setAlerts] = useState(false);
  const [draft, setDraft] = useState({ dateFrom: todayIso(), dateTo: todayIso() });
  const [applied, setApplied] = useState(draft);
  const panel = useQuery({
    queryKey: ['dashboard', 'monitoring', applied],
    queryFn: () => api.get<MonitoringPanelDto>('/dashboard/monitoring', applied),
    refetchInterval: FIVE_MINUTES,
    enabled: user?.level === 'admin',
  });
  if (user?.level !== 'admin' && !can('PC965'))
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        The Monitoring Panel is for administrators and for groups given PC965.
      </Alert>
    );
  return (
    <Box>
      <PageHeader
        title="Monitoring Panel"
        actions={
          featureOn('monitoring-alerts') ? (
            <Button variant="contained" onClick={() => setAlerts(true)}>
              Monitoring Panel Email Notification Setting
            </Button>
          ) : null
        }
      />
      {alerts ? <AlertSettingsDialog open onClose={() => setAlerts(false)} /> : null}
      <FilterPanel
        onSearch={() => setApplied(draft)}
        onReset={() => {
          const today = { dateFrom: todayIso(), dateTo: todayIso() };
          setDraft(today);
          setApplied(today);
        }}
      >
        <TextField
          type="date"
          label="Date Range: from"
          value={draft.dateFrom}
          onChange={(e) => setDraft((d) => ({ ...d, dateFrom: e.target.value }))}
          {...DATE}
        />
        <TextField
          type="date"
          label="to"
          value={draft.dateTo}
          onChange={(e) => setDraft((d) => ({ ...d, dateTo: e.target.value }))}
          {...DATE}
        />
      </FilterPanel>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        *This page will automatically refresh every 5 minutes.
      </Typography>
      {panel.error ? (
        <ErrorState error={panel.error} onRetry={() => void panel.refetch()} />
      ) : !panel.data ? (
        <LoadingState />
      ) : (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Paper
            variant="outlined"
            component="section"
            aria-label="Activity Success Rate"
            sx={{ flex: '1 1 360px' }}
          >
            <Typography variant="h3" component="h2" sx={{ px: 1.5, py: 1 }}>
              Activity Success Rate
            </Typography>
            <Table size="small" aria-label="Activity Success Rate">
              <TableHead>
                <TableRow>
                  <TableCell>Process Name</TableCell>
                  <TableCell align="right">Success</TableCell>
                  <TableCell align="right">Failure</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {panel.data.activity.map((a) => (
                  <TableRow key={a.process}>
                    <TableCell>{a.process}</TableCell>
                    <TableCell align="right">{a.success}</TableCell>
                    <TableCell align="right">{a.failure}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          <DayTable title="Scheduled Autopay" rows={panel.data.scheduledAutopay} />
          <DayTable title="Scheduled Renewals" rows={panel.data.scheduledRenewals} />
        </Box>
      )}
    </Box>
  );
}
